import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Calendar } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { formatAmount, getCurrencySymbol } from '../utils/formatters';
import { useI18n } from '../hooks/useI18n';

interface MonthlyBreakdown {
    month: string;
    amount: number;
    cumulative_amount: number;
    remaining_amount: number;
}

interface RecognitionSchedule {
    id: string;
    invoice_id: string;
    recognition_type: 'immediate' | 'over_time';
    start_date: string;
    end_date: string;
    total_amount: number;
    status: 'draft' | 'active' | 'completed' | 'cancelled';
    notes?: string;
    monthly_breakdown?: MonthlyBreakdown[];
}

interface RecognitionScheduleModalProps {
    open: boolean;
    onClose: () => void;
    invoiceId: string;
    invoiceAmount: number;
    invoiceCurrency: string;
    invoiceVendor?: string;
    invoiceNumber?: string;
    onSaved?: () => void;
}

export default function RecognitionScheduleModal({
    open,
    onClose,
    invoiceId,
    invoiceAmount,
    invoiceCurrency,
    invoiceVendor,
    invoiceNumber,
    onSaved
}: RecognitionScheduleModalProps) {
    const { t } = useI18n();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [schedule, setSchedule] = useState<RecognitionSchedule | null>(null);

    // Form state
    const [recognitionType, setRecognitionType] = useState<'immediate' | 'over_time'>('over_time');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [totalAmount, setTotalAmount] = useState(invoiceAmount.toString());
    const [notes, setNotes] = useState('');
    const [monthlyBreakdown, setMonthlyBreakdown] = useState<MonthlyBreakdown[]>([]);

    // Load existing schedule
    useEffect(() => {
        if (open && invoiceId) {
            loadSchedule();
        }
    }, [open, invoiceId]);

    const loadSchedule = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiFetch(`/api/recognition-schedules/invoice/${invoiceId}`);
            if (response.ok) {
                const data = await response.json();
                if (data) {
                    setSchedule(data);
                    setRecognitionType(data.recognition_type);
                    setStartDate(data.start_date);
                    setEndDate(data.end_date);
                    setTotalAmount(data.total_amount.toString());
                    setNotes(data.notes || '');
                    setMonthlyBreakdown(data.monthly_breakdown || []);
                } else {
                    // No schedule exists yet
                    setSchedule(null);
                    // Set default start date to current month, leave end date empty
                    const today = new Date();
                    setStartDate(today.toISOString().split('T')[0]);
                    setEndDate('');  // Empty end date initially
                }
            }
        } catch (err) {
            console.error('Error loading schedule:', err);
            setError(t('recognition.failedToLoad', 'finance'));
        } finally {
            setLoading(false);
        }
    };

    const calculateBreakdown = async () => {
        // Only calculate if we have start date, end date, and total amount
        if (!startDate || !endDate || !totalAmount) {
            setMonthlyBreakdown([]);
            return;
        }

        // Parse dates from YYYY-MM-DD format to avoid timezone issues
        const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = endDate.split('-').map(Number);

        const start = new Date(startYear, startMonth - 1, startDay);
        const end = new Date(endYear, endMonth - 1, endDay);

        // Validate that end date is after start date
        if (end < start) {
            setMonthlyBreakdown([]);
            return;
        }

        const months: MonthlyBreakdown[] = [];

        let current = new Date(start.getFullYear(), start.getMonth(), 1);
        const endMonth_date = new Date(end.getFullYear(), end.getMonth(), 1);

        while (current <= endMonth_date) {
            // Format as YYYY-MM
            const year = current.getFullYear();
            const month = String(current.getMonth() + 1).padStart(2, '0');
            months.push({
                month: `${year}-${month}`,
                amount: 0,
                cumulative_amount: 0,
                remaining_amount: 0
            });
            current.setMonth(current.getMonth() + 1);
        }

        const numMonths = months.length;
        if (numMonths === 0) {
            setMonthlyBreakdown([]);
            return;
        }

        const amountPerMonth = parseFloat(totalAmount) / numMonths;
        let cumulative = 0;

        months.forEach((month, index) => {
            cumulative += amountPerMonth;
            month.amount = amountPerMonth;
            month.cumulative_amount = cumulative;
            month.remaining_amount = parseFloat(totalAmount) - cumulative;
        });

        setMonthlyBreakdown(months);
    };

    // Recalculate breakdown when dates or amount change
    useEffect(() => {
        if (recognitionType === 'over_time') {
            calculateBreakdown();
        }
    }, [startDate, endDate, totalAmount, recognitionType]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            const payload = {
                invoice_id: invoiceId,
                recognition_type: recognitionType,
                start_date: startDate,
                end_date: endDate,
                total_amount: parseFloat(totalAmount),
                status: 'active',  // Always set to active
                notes: notes || null
            };

            let response;
            if (schedule) {
                // Update existing schedule
                response = await apiFetch(`/api/recognition-schedules/${schedule.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                // Create new schedule
                response = await apiFetch('/api/recognition-schedules', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            if (response.ok) {
                const data = await response.json();
                setSchedule(data);
                setMonthlyBreakdown(data.monthly_breakdown || []);
                if (onSaved) {
                    onSaved();
                }
                onClose();
            } else {
                const errorData = await response.json();
                setError(errorData.detail || t('recognition.failedToSave', 'finance'));
            }
        } catch (err) {
            console.error('Error saving schedule:', err);
            setError(t('recognition.failedToSave', 'finance'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!schedule || !confirm('Are you sure you want to delete this recognition schedule?')) {
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const response = await apiFetch(`/api/recognition-schedules/${schedule.id}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setSchedule(null);
                if (onSaved) {
                    onSaved();
                }
                onClose();
            } else {
                setError(t('recognition.failedToDelete', 'finance'));
            }
        } catch (err) {
            console.error('Error deleting schedule:', err);
            setError(t('recognition.failedToDelete', 'finance'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        {t('recognition.schedule', 'finance')}
                    </DialogTitle>
                    {invoiceVendor && (
                        <p className="text-sm text-muted-foreground">
                            {invoiceVendor} - {invoiceNumber || t('recognition.noInvoiceNumber', 'finance')}
                        </p>
                    )}
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label>{t('units.total', 'common')} ({invoiceCurrency})</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={totalAmount}
                                onChange={(e) => setTotalAmount(e.target.value)}
                                placeholder="10000.00"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t('recognition.startDate', 'finance')}</Label>
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>{t('recognition.endDate', 'finance')}</Label>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        {monthlyBreakdown.length > 0 && startDate && endDate && (
                            <div className="space-y-2">
                                <Label>{t('recognition.monthlyBreakdown', 'finance')} ({invoiceCurrency})</Label>
                                <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>{t('recognition.period', 'finance')}</TableHead>
                                                <TableHead className="text-right">{t('recognition.expenseIncurred', 'finance')}</TableHead>
                                                <TableHead className="text-right">{t('recognition.cumulativeExpense', 'finance')}</TableHead>
                                                <TableHead className="text-right">{t('recognition.prepaidExpense', 'finance')}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {monthlyBreakdown.map((row, index) => {
                                                const currencySymbol = getCurrencySymbol(invoiceCurrency);
                                                return (
                                                    <TableRow key={index}>
                                                        <TableCell>{row.month}</TableCell>
                                                        <TableCell className="text-right">
                                                            {formatAmount(row.amount, { currency: currencySymbol, decimals: 2 })}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {formatAmount(row.cumulative_amount, { currency: currencySymbol, decimals: 2 })}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {formatAmount(row.remaining_amount, { currency: currencySymbol, decimals: 2 })}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {!endDate && startDate && (
                            <Alert>
                                <AlertDescription>
                                    {t('recognition.selectEndDate', 'finance')}
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {schedule && (
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={saving}
                        >
                            {t('actions.delete', 'common')}
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        {t('actions.cancel', 'common')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving || loading}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {schedule ? t('actions.update', 'common') : t('actions.create', 'common')} {t('recognition.scheduleAction', 'finance')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
